

import { SectionCard } from "./SectionCard";
import { Financials, VendorTotals, ServiceTotals, fmt } from "./types";

interface PnLSummaryProps {
  financials: Financials;
  vendorTotals: VendorTotals;
  serviceTotals: ServiceTotals;
}

export function PnLSummary({ financials, vendorTotals, serviceTotals }: PnLSummaryProps) {
  const f = financials;
  const profit = f.profit ?? 0;
  const netRevenue = f.net_revenue ?? 0;
  const profitMargin = netRevenue > 0 ? ((profit / netRevenue) * 100).toFixed(1) : "0.0";

  const statCards = [
    { label: "Revenue (before GST)", val: fmt(f.net_revenue), bg: "bg-stone-50", text: "text-stone-800" },
    { label: "Service Cost", val: fmt(serviceTotals.total_cost), bg: "bg-blue-50", text: "text-blue-700" },
    { label: "Vendor Cost", val: fmt(vendorTotals.total_assigned), bg: "bg-rose-50", text: "text-rose-700" },
    { label: "Owed to vendors", val: fmt(vendorTotals.total_balance), bg: "bg-amber-50", text: "text-amber-700" },
  ];

  const rows = [
    { label: "Revenue (after discount, before GST)", value: fmt(f.net_revenue), bold: false, indent: false },
    null,
    { label: "Service Cost (Managed Services)", value: `- ${fmt(serviceTotals.total_cost)}`, bold: false, indent: true },
    { label: "Vendor Cost (Vendor Outsourcing)", value: `- ${fmt(vendorTotals.total_assigned)}`, bold: false, indent: true },
    { label: "Additional Expenses", value: `- ${fmt(f.adhoc_cost)}`, bold: false, indent: true },
    null,
    {
      label: profit >= 0 ? "Profit" : "Loss",
      value: fmt(Math.abs(profit)),
      bold: true,
      indent: false,
      highlight: profit >= 0 ? "text-emerald-700" : "text-rose-600",
    },
    {
      label: "Profit Margin",
      value: `${profitMargin}%`,
      bold: false,
      indent: true,
      highlight: profit >= 0 ? "text-emerald-600" : "text-rose-500",
    },
  ];

  return (
    <SectionCard
      title="Profit & Loss Summary"
      icon={
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      }
    >
      {/* Revenue breakdown */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-blue-50 rounded-xl p-3 border border-blue-100">
          <div className="text-xs text-blue-500 font-semibold mb-1">Service Revenue</div>
          <div className="text-base font-bold text-blue-700">{fmt(serviceTotals.total_revenue)}</div>
        </div>
        <div className="bg-violet-50 rounded-xl p-3 border border-violet-100">
          <div className="text-xs text-violet-500 font-semibold mb-1">Vendor Revenue</div>
          <div className="text-base font-bold text-violet-700">{fmt(f.vendor_revenue)}</div>
        </div>
      </div>

      <div className="max-w-md space-y-2 text-sm">
        {rows.map((row, i) => {
          if (row === null) return <div key={i} className="border-t border-stone-200 my-1" />;
          const r = row as any;
          return (
            <div key={i} className={`flex justify-between items-center ${r.indent ? "pl-4" : ""} ${r.bold ? "font-bold text-base pt-1" : ""}`}>
              <span className={r.highlight ?? "text-stone-600"}>{r.label}</span>
              <span className={r.highlight ?? "text-stone-800"}>{r.value}</span>
            </div>
          );
        })}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        {statCards.map(({ label, val, bg, text }) => (
          <div key={label} className={`rounded-xl p-3 border border-stone-100 ${bg}`}>
            <div className={`text-base font-bold ${text} truncate`}>{val}</div>
            <div className="text-xs text-stone-400 mt-0.5 truncate">{label}</div>
          </div>
        ))}
      </div>

      {/* Vendor paid highlight */}
      {vendorTotals.total_paid > 0 && (
        <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-xl border border-emerald-100">
          <span className="text-xs font-semibold text-emerald-700">Vendor Payments Made</span>
          <span className="text-xs font-bold text-emerald-700 ml-auto">{fmt(vendorTotals.total_paid)}</span>
        </div>
      )}
    </SectionCard>
  );
}
